import { useCallback, useEffect, useState } from 'react'
import { CiLink } from 'react-icons/ci'
import { Link } from 'react-router-dom'

import { PageNavigation } from '~/components/PageNavigation'
import { listDiscounts, numberToBRL } from '~/modules/sales/services'
import { Discount } from '~/modules/sales/types'
import { debouncer } from '~/utils/debounce'
import { getLocalDate } from '~/utils/getLocalDate'

import { CreateDiscountModal } from '../components/CreateDiscountModal'
import { DeleteDiscountButton } from '../components/DeleteDiscountButton'

type FilterQuery = {
  page: number
  size: number
}

let abortController = new AbortController()
const debounce = debouncer()

export const Discounts: React.FC = () => {
  const [discounts, setDiscounts] = useState<Discount[]>([])
  const [totalItems, setTotalItems] = useState<number>(0)
  const [filterQuery, setFilterQuery] = useState<FilterQuery>({
    page: 0,
    size: 10,
  })

  const handleUpdatePage = useCallback(async (page: number) => {
    setFilterQuery((state) => ({ ...state, page }))
  }, [])

  const handleGetDiscounts = useCallback(() => {
    abortController = new AbortController()
    listDiscounts(filterQuery, abortController).then((data) => {
      if (data) {
        setDiscounts(data)
        setTotalItems(data.length)
      }
    })
  }, [filterQuery])

  useEffect(() => {
    abortController = new AbortController()
    debounce(() => {
      listDiscounts(filterQuery, abortController).then((data) => {
        if (data) {
          setDiscounts(data)
        }
      })
    })

    return () => {
      abortController.abort()
    }
  }, [filterQuery])

  return (
    <div
      className='flex flex-col flex-1 gap-4 justify-end m-5 p-5 bg-white
      rounded-xl overflow-hidden'
    >
      <div className='flex flex-col gap-2'>
        <CreateDiscountModal onSuccess={handleGetDiscounts} />
      </div>

      <div className='max-w-full flex-1 overflow-hidden overflow-x-auto'>
        <table className='w-full min-w-[1050px]'>
          <thead>
            <tr className='text-left'>
              <th>Código</th>
              <th>Tipo</th>
              <th>Plano?</th>
              <th>Valor</th>
              <th>Expira</th>
              <th>Limite</th>
              <th>Usados</th>
              <th>Criado</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {discounts.map((d) => (
              <tr key={d.id} className='even:bg-gpa-blue-50'>
                <td className='py-2'>{d.code}</td>
                <td className='py-2'>
                  <span
                    data-type={d.type}
                    className='font-bold px-2 py-1 rounded-md
                    text-gpa-blue-500 bg-gpa-blue-50
                    '
                  >
                    {d.type === 'percentage' ? 'Porcentagem' : 'Valor Fixo'}
                  </span>
                </td>
                <td className='py-2'>
                  {d.plan ? (
                    <Link
                      to={`/admin/vendas/planos?plano=${encodeURIComponent(
                        d.plan.name,
                      ).toString()}`}
                      className='whitespace-nowrap flex items-center gap-1'
                    >
                      S
                      <CiLink size={22} />
                    </Link>
                  ) : (
                    'N'
                  )}
                </td>
                <td className='py-2'>
                  {d.type === 'percentage'
                    ? d.value + '%'
                    : numberToBRL(d.value / 100)}
                </td>
                <td
                  data-valid={d.valid}
                  className='py-2 data-[valid=false]:text-gpa-red'
                >
                  {d.valid ? getLocalDate(d.expiresAt) : 'Invalidado'}
                </td>
                <td>{d.limit ?? 'N'}</td>
                <td>{d._count.subscriptionDiscount}</td>
                <td className='py-2'>{getLocalDate(d.createdAt)}</td>
                <td className='flex gap-2 py-2'>
                  {d.valid ? (
                    <DeleteDiscountButton
                      discountId={d.id}
                      onSuccess={handleGetDiscounts}
                    />
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className='self-end'>
        <PageNavigation
          numPages={Math.ceil(totalItems / filterQuery.size)}
          currentPage={filterQuery.page}
          onPageChange={handleUpdatePage}
        />
      </div>
    </div>
  )
}
